/**
 * Bekannte Bankverbindungen eines Geschäftspartners.
 *
 * Die Liste wird nicht gepflegt, sondern gelernt: Jede gebuchte Zuordnung
 * im Kontoauszug merkt sich die Gegen-IBAN. Beim nächsten Auszug erkennt
 * Canalis den Partner daran, auch ohne Belegnummer im Verwendungszweck.
 * Eine falsch gelernte IBAN lässt sich hier wieder entfernen.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatDate, formatIban } from '@erp/shared';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from './toast';
import { EmptyState, LoadingBlock } from './ui';

export type PartnerBankAccount = {
  id: string;
  partnerId: string;
  iban: string;
  bic: string | null;
  accountHolder: string | null;
  firstSeenOn: string | null;
  lastSeenOn: string | null;
  useCount: number;
};

export function PartnerBankAccounts({ partnerId }: { partnerId: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayWrite = can('masterdata:write');

  const { data, isLoading } = useQuery({
    queryKey: ['partner-bank-accounts', partnerId],
    queryFn: () =>
      api.get<{ bankAccounts: PartnerBankAccount[] }>(`/api/partners/${partnerId}/bank-accounts`),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/partners/${partnerId}/bank-accounts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-bank-accounts', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      toast.success('Die Bankverbindung wurde entfernt.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Entfernen fehlgeschlagen.'),
  });

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2>Bankverbindungen</h2>
          <div className="faint" style={{ fontSize: 12.5 }}>
            Aus gebuchten Kontoauszügen gelernt — der Import erkennt den Partner daran auch ohne
            Belegnummer im Verwendungszweck
          </div>
        </div>
      </div>

      {isLoading ? <LoadingBlock /> : null}

      {data ? (
        data.bankAccounts.length === 0 ? (
          <EmptyState
            title="Noch keine Bankverbindung bekannt"
            hint="Sobald eine Zahlung dieses Partners im Kontoauszug zugeordnet wird, steht das Konto hier."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>IBAN</th>
                  <th className="shrink">BIC</th>
                  <th>Kontoinhaber</th>
                  <th className="shrink">Zuletzt</th>
                  <th className="shrink num">Zahlungen</th>
                  {mayWrite ? <th className="shrink"></th> : null}
                </tr>
              </thead>
              <tbody>
                {data.bankAccounts.map((account) => (
                  <tr key={account.id}>
                    <td className="mono">{formatIban(account.iban)}</td>
                    <td className="faint mono">{account.bic ?? ''}</td>
                    <td>{account.accountHolder ?? ''}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(account.lastSeenOn)}</td>
                    <td className="num">{account.useCount}</td>
                    {mayWrite ? (
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(account.id)}
                        >
                          Entfernen
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}
